/**
 * State abbreviation utility
 * Maps full state names to their 2-letter USPS abbreviations
 */

export interface StateInfo {
  name: string;
  abbreviation: string;
}

export const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  "District of Columbia": "DC",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

/**
 * Convert full state name to 2-letter abbreviation
 * Industry best practice: Use USPS standard abbreviations for consistency
 * @param stateName - Full state name (e.g., "Minnesota")
 * @returns 2-letter abbreviation (e.g., "MN") or the original if not found
 */
export const getStateAbbreviation = (stateName: string): string => {
  if (!stateName) return "";

  // If already an abbreviation (2 letters), return as-is
  if (stateName.length === 2) {
    return stateName.toUpperCase();
  }

  // Try to find abbreviation
  const trimmed = stateName.trim();
  return STATE_ABBREVIATIONS[trimmed] || trimmed;
};

/**
 * Convert state abbreviation to full name
 * @param abbreviation - 2-letter state abbreviation (e.g., "MN")
 * @returns Full state name (e.g., "Minnesota") or the original if not found
 */
export const getStateName = (abbreviation: string): string => {
  if (!abbreviation) return "";

  const trimmed = abbreviation.trim().toUpperCase();

  // Find state name by abbreviation
  for (const [name, abbrev] of Object.entries(STATE_ABBREVIATIONS)) {
    if (abbrev === trimmed) {
      return name;
    }
  }

  return abbreviation;
};

/**
 * Check if a string is a valid state abbreviation
 */
export const isValidStateAbbreviation = (abbreviation: string): boolean => {
  return Object.values(STATE_ABBREVIATIONS).includes(
    abbreviation.toUpperCase()
  );
};

/**
 * Get state information object
 */
export const getStateInfo = (state: string): StateInfo | null => {
  if (!state) return null;

  // Check if input is already an abbreviation
  if (state.length === 2) {
    const name = getStateName(state);
    if (name && name !== state) {
      return { name, abbreviation: state.toUpperCase() };
    }
  }

  // Check if input is a full state name
  const abbrev = getStateAbbreviation(state);
  if (abbrev && abbrev !== state && abbrev.length === 2) {
    return { name: state, abbreviation: abbrev };
  }

  return null;
};
